import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import LoadingButton from '@mui/lab/LoadingButton';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Grid from '@mui/material/Unstable_Grid2';
import Typography from '@mui/material/Typography';
import FormControlLabel from '@mui/material/FormControlLabel';
// utils
import { fData } from 'src/utils/format-number';
// routes
// assets
import { countries } from 'src/assets/data';
// components
import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import { useSnackbar } from 'src/components/snackbar';
import FormProvider, {
    RHFTextField,
    RHFUploadAvatar,
    RHFAutocomplete,
    RHFSelect
} from 'src/components/hook-form';
import { IconButton, InputAdornment } from '@mui/material';
import { useBoolean } from 'src/hooks/use-boolean';
import { getRequest, postRequest } from 'src/services';
import { handleErrorMessages } from 'src/services/fetching';
import { endpoints } from 'src/utils/axios';
import { useLocation, useNavigate } from 'react-router';
import RHFPhoneInput from 'src/components/hook-form/rhft-phone-number';

// ----------------------------------------------------------------------

export default function UserNewForm({ currentUser }) {
    const password = useBoolean();
    const [ loading, setLoading ] = useState(false);
    const [ roles, setRoles ] = useState();
    const { enqueueSnackbar } = useSnackbar();
    const location = useLocation();
    const row = location.state;


    const navigate = useNavigate()
    const NewUserSchema = Yup.object().shape({
        first_name: Yup.string().required('First Name is required'),
        last_name: Yup.string().required('Last Name is required'),
        email: Yup.string()
            .required('Email is required')
            .email('Email must be a valid email address'),
        phone: Yup.string().required('Phone number is required'),
        // city: Yup.string().required('City is required'),
        // state: Yup.string().required('State is required'),
        country: Yup.string().required('Country is required'),
        // address: Yup.string().required('Address is required'),
        // status: Yup.string().required('Status is required'),
        // password: Yup.string()
        //     .required('Password is required')
        //     .min(8, 'Password must be at least 8 characters'),
        zip_code: Yup.string()
            .required('Zip Code is required')
            .matches(/^\d+$/, 'Zip Code must be numeric'),
        role: Yup.string().required('Role is required'),
        // avatarUrl: Yup.mixed()
        //     .nullable()
        //     .required('Avatar is required')
        //     .test('fileSize', 'File size must be less than 3MB', (value) =>
        //         value ? value.size <= 3145728 : true
        //     )
        //     .test('fileType', 'Only .jpeg, .jpg, .png, and .gif formats are allowed', (value) =>
        //         value ? [ 'image/jpeg', 'image/jpg', 'image/png', 'image/gif' ].includes(value.type) : true
        //     ),
    });



    useEffect(() => {
        getRolesAndPermisions();
    }, []);

    const getRolesAndPermisions = async () => {
        const data = await getRequest(`${endpoints.admin_dashboard.roles_and_permissions.get_list}`);
        if (data) {
            console.log("🚀 ~ getRolesAndPermisions ~ data:", data)
            setRoles(data || []);
        }
    };


    const defaultValues = useMemo(
        () => ({
            first_name: row?.first_name || '',
            last_name: row?.last_name || '',
            email: row?.email || '',
            phone: row?.phone || '',
            city: row?.city || '',
            state: row?.state || '',
            country: row?.country || '',
            address: row?.address || '',
            status: row?.status?.value || 1,
            password: row?.password || '',
            zip_code: row?.zip_code || '',
            role: row?.roles?.length ? row.roles[ 0 ].id.toString() : '',
        }),
        [ row ]
    );

    const methods = useForm({
        resolver: yupResolver(NewUserSchema),
        defaultValues,
    });

    const {
        reset,
        watch,
        control,
        setValue,
        handleSubmit,
        formState: { isSubmitting },
    } = methods;

    const values = watch();
    const onSubmit = handleSubmit(async (data) => {
        setLoading(true);
        const formData = new FormData();
        formData.append('first_name', data.first_name);
        formData.append('last_name', data.last_name);
        formData.append('email', data.email);
        formData.append('phone', data.phone);
        formData.append('city', data.city);
        formData.append('state', data.state);
        formData.append('country', data.country);
        formData.append('address', data.address);
        formData.append('status', data.status || 1);
        !row?.id && formData.append('password', data.password || '');
        formData.append('zip_code', data.zip_code);
        formData.append('role_id', data.role);

        try {
            const response = await postRequest(
                row?.id
                    ? `${endpoints.admin_dashboard.admin_user.post_user}/${row.id}?_method=PUT`
                    : endpoints.admin_dashboard.admin_user.post_user,
                formData,
                true
            );

            if (response.metadata?.success) {
                const roleNavigation = {
                    1: '/dashboard/admin-users/super-admin',
                    2: '/dashboard/admin-users/marketing',
                    3: '/dashboard/admin-users/leads',
                    4: '/dashboard/admin-users/sales',
                    5: '/dashboard/admin-users/demo',
                    6: '/dashboard/admin-users/technical',
                };

                // Default path for other roles
                const defaultPath = '/dashboard/admin-users/others';

                // Determine the navigation path
                const navigateTo = roleNavigation[ data.role ] || defaultPath;

                navigate(navigateTo);

                reset({
                    first_name: '',
                    last_name: '',
                    email: '',
                    phone: '',
                    city: '',
                    state: '',
                    country: '',
                    address: '',
                    status: '',
                    password: '',
                    zip_code: '',
                    role: '',
                    avatarUrl: null,
                });

                enqueueSnackbar({
                    variant: 'success',
                    message: response.metadata.message,
                });
            } else {
                handleErrorMessages(response?.metadata?.message || 'An error occurred.');
            }
        } catch (err) {
            console.error('Error:', err);
            handleErrorMessages(err.message || 'Submission failed.');
        } finally {
            setLoading(false);
        }
    });


    const handleDrop = useCallback(
        (acceptedFiles) => {
            const file = acceptedFiles[ 0 ];

            const newFile = Object.assign(file, {
                preview: URL.createObjectURL(file),
            });

            if (file) {
                setValue('avatarUrl', newFile, { shouldValidate: true });
            }
        },
        [ setValue ]
    );

    const status = [
        { value: '1', label: 'Active' },
        { value: '2', label: 'Inactive' },
        { value: '3', label: 'Pending' },
    ]


    return (
        <FormProvider methods={ methods } onSubmit={ onSubmit }>
            <Grid container spacing={ 3 }>
                <Grid xs={ 12 } md={ 4 }>
                    <Card sx={ { pt: 10, pb: 5, px: 3 } }>
                        { currentUser && (
                            <Label
                                color={
                                    (values.status === 'active' && 'success') ||
                                    (values.status === 'banned' && 'error') ||
                                    'warning'
                                }
                                sx={ { position: 'absolute', top: 24, right: 24 } }
                            >
                                { values?.status }
                            </Label>
                        ) }

                        <Box sx={ { mb: 5 } }>
                            <RHFUploadAvatar
                                name="avatarUrl"
                                maxSize={ 3145728 }
                                onDrop={ handleDrop }
                                helperText={
                                    <Typography
                                        variant="caption"
                                        sx={ {
                                            mt: 3,
                                            mx: 'auto',
                                            display: 'block',
                                            textAlign: 'center',
                                            color: 'text.disabled',
                                        } }
                                    >
                                        Allowed *.jpeg, *.jpg, *.png, *.gif
                                        <br /> max size of { fData(3145728) }
                                    </Typography>
                                }
                            />
                        </Box>

                        { currentUser && (
                            <FormControlLabel
                                labelPlacement="start"
                                control={
                                    <Controller
                                        name="status"
                                        control={ control }
                                        render={ ({ field }) => (
                                            <Switch
                                                { ...field }
                                                checked={ field.value !== 'active' }
                                                onChange={ (event) =>
                                                    field.onChange(event.target.checked ? 'banned' : 'active')
                                                }
                                            />
                                        ) }
                                    />
                                }
                                label={
                                    <>
                                        <Typography variant="subtitle2" sx={ { mb: 0.5 } }>
                                            Banned
                                        </Typography>
                                        <Typography variant="body2" sx={ { color: 'text.secondary' } }>
                                            Apply disable account
                                        </Typography>
                                    </>
                                }
                                sx={ { mx: 0, mb: 3, width: 1, justifyContent: 'space-between' } }
                            />
                        ) }


                    </Card>
                </Grid>
                <Grid xs={ 12 } md={ 8 }>
                    <Card sx={ { p: 3 } }>
                        <Box
                            rowGap={ 3 }
                            columnGap={ 2 }
                            display="grid"
                            gridTemplateColumns={ {
                                xs: 'repeat(1, 1fr)',
                                sm: 'repeat(2, 1fr)',
                            } }
                        >
                            <RHFTextField name="first_name" label="First Name" />
                            <RHFTextField name="last_name" label="Last Name" />
                            <RHFTextField name="email" label="Email Address" />
                            <RHFPhoneInput name="phone" label="Phone" />

                            <RHFSelect
                                native
                                name="status"
                                label="Status"
                                InputLabelProps={ { shrink: true } }
                            >
                                { status.map((item) => (
                                    <option key={ item.value } value={ item.value }>
                                        { item.label }
                                    </option>
                                )) }
                            </RHFSelect>
                            <RHFAutocomplete
                                name="country"
                                label="Country"
                                options={ countries.map((country) => country.label) }
                                getOptionLabel={ (option) => option }
                                isOptionEqualToValue={ (option, value) => option === value }
                                renderOption={ (props, option) => {
                                    const { code, label, phone } = countries.filter(
                                        (country) => country.label === option
                                    )[ 0 ];

                                    if (!label) {
                                        return null;
                                    }
                                    return (
                                        <li { ...props } key={ label }>
                                            <Iconify
                                                key={ label }
                                                icon={ `circle-flags:${code.toLowerCase()}` }
                                                width={ 28 }
                                                sx={ { mr: 1 } }
                                            />
                                            { label } ({ code }) +{ phone }
                                        </li>
                                    );
                                } }
                            />
                            <RHFTextField name="state" label="State/Region" />
                            <RHFTextField name="city" label="City" />
                            <RHFTextField name="address" label="Address" />
                            <RHFTextField name="zip_code" label="Zip/Code" />
                            <RHFSelect
                                native
                                name="role"
                                label="Role"
                                InputLabelProps={ { shrink: true } }
                            >
                                { roles?.length > 0 ? (
                                    roles.map((item) => (
                                        <option key={ item.id } value={ item.id }>
                                            { item.name }
                                        </option>
                                    ))
                                ) : (
                                    <option value="">Select Role</option>
                                ) }
                            </RHFSelect>

                            { !row?.id &&
                                <RHFTextField
                                    name="password"
                                    label="Password"
                                    type={ password.value ? 'text' : 'password' }
                                    InputProps={ {
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton onClick={ password.onToggle } edge="end">
                                                    <Iconify icon={ password.value ? 'solar:eye-bold' : 'solar:eye-closed-bold' } />
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    } }
                                />
                            }
                        </Box>
                        <Stack alignItems="flex-end" sx={ { mt: 3 } }>
                            <LoadingButton type="submit" variant="contained" loading={ isSubmitting }>
                                { !row?.id ? 'Create User' : 'Update User' }
                            </LoadingButton>
                        </Stack>
                    </Card>
                </Grid>
            </Grid>
        </FormProvider>
    );
}

UserNewForm.propTypes = {
    currentUser: PropTypes.object,
};
